import { useCallback, useEffect, useMemo } from 'react';
import usePermission from 'react-use/esm/usePermission';
import { atom, useSetRecoilState, useRecoilValue } from 'recoil';
import type { RecoilState } from 'recoil';
import {
  broadcastChannelSynchronizerEffect,
  localStoragePersisterEffect,
} from 'utils/recoilEffects';
import { SRNA_PERSIST_TOKEN } from 'config/constants';
import { useMediaDevices } from '../useMediaDevices';
import { cleanInputLabel } from './utils';
import { WithApolloContext } from 'modules/Apollo/types';
import {
  isVirtualDictaphoneAudioInputEnabled,
  VIRTUAL_DICTAPHONE_AUDIO_INPUT_DEVICE_ID,
  VIRTUAL_DICTAPHONE_AUDIO_INPUT_LABEL,
} from 'utils/dictaphone/products/virtual';
import { identifyDictaphoneProducts } from 'utils/dictaphone/products';
import { useToolPreferencesQuery } from 'hooks/usePreferences';

export type MicrophoneState = {
  audioInputDeviceId: string;
  audioInputDeviceLabel: string;
  userDescribedDictaphone: boolean;
};

const defaultValue = {
  audioInputDeviceId: 'default',
  audioInputDeviceLabel: '',
  userDescribedDictaphone: false,
} as const;

export const microphoneState: RecoilState<MicrophoneState> = atom<MicrophoneState>({
  key: `${SRNA_PERSIST_TOKEN}microphoneState`,
  default: defaultValue,
  effects: [broadcastChannelSynchronizerEffect(), localStoragePersisterEffect()],
});

const withVirtualDictaphoneAudioInput = (
  availableAudioInputs: Array<MediaDeviceInfo>
): Array<MediaDeviceInfo> => {
  const hasVirtualInput = availableAudioInputs.some(
    ({ deviceId }) => deviceId === VIRTUAL_DICTAPHONE_AUDIO_INPUT_DEVICE_ID
  );
  if (hasVirtualInput) {
    return availableAudioInputs;
  }
  return [
    ...availableAudioInputs,
    {
      deviceId: VIRTUAL_DICTAPHONE_AUDIO_INPUT_DEVICE_ID,
      groupId: '',
      kind: 'audioinput',
      label: VIRTUAL_DICTAPHONE_AUDIO_INPUT_LABEL,
    },
  ];
};

/**
 * If the microphone permission changes, the media devices list won't
 * update. This hook triggers an event that will refresh the list.
 *
 * @see https://github.com/streamich/react-use/issues/1318
 */
export function useRefreshMediaDevices() {
  const micPermission = usePermission({ name: 'microphone' });
  useEffect(() => {
    const { mediaDevices } = navigator;
    if (mediaDevices == null) return;

    // This call only exists to prompt for microphone permission so the devicechange event
    // below reflects an updated device list. Errors are suppressed to avoid unhandled rejections.
    mediaDevices
      .getUserMedia({ audio: true })
      .catch(() => {})
      .finally(() => {
        mediaDevices.dispatchEvent(new Event('devicechange'));
      });
  }, [micPermission]);
}

export type MediaDeviceInfo = {
  deviceId: string;
  groupId: string;
  kind: string;
  label: string;
};

export type UseMicrophoneReturn = {
  audioInputDevice: MediaDeviceInfo | undefined;
  audioInputDeviceId: string;
  audioInputDeviceLabel: string;
  audioInputDevices: Array<MediaDeviceInfo>;
  setAudioInputDevice: (audioInputDeviceId: string, audioInputDeviceLabel: string) => void;
  isKnownDictaphoneProduct: boolean;
  isDictaphone: boolean;
  userDescribedDictaphone: boolean;
  setUserDescribedDictaphone: (userDescribedDictaphone: boolean) => void;
  isLoaded: boolean;
};

export function useMicrophone({ context }: WithApolloContext): UseMicrophoneReturn {
  const { devices, loaded } = useMediaDevices();
  const { audioInputDeviceId, userDescribedDictaphone } = useRecoilValue(microphoneState);
  const setMicrophoneState = useSetRecoilState(microphoneState);

  // We want to know whether or not the active input device is a dictaphone.
  // The first time the user plugs in a dictaphone, they need to tell us. But after that, it is persisted in the user's config state.
  // Long term, we can centralize a list of known dictaphones and their calibrations and hopefully avoid some of tedium of setup.
  const { data } = useToolPreferencesQuery();
  const dictaphoneSettings = data?.toolPreferences?.dictaphone;
  const audioInputDevices = useMemo(() => {
    const availableAudioInputs =
      devices?.filter((device: MediaDeviceInfo) => device.kind === 'audioinput') ?? [];
    if (!isVirtualDictaphoneAudioInputEnabled()) {
      return availableAudioInputs;
    }
    return withVirtualDictaphoneAudioInput(availableAudioInputs);
  }, [devices]);
  const audioInputDevice = useMemo(
    () => audioInputDevices.find((device) => device.deviceId === audioInputDeviceId),
    [audioInputDevices, audioInputDeviceId]
  );
  const audioInputDeviceLabel = audioInputDevice?.label ?? '';
  const cleanedInputLabel = cleanInputLabel(audioInputDeviceLabel);
  const cleanedSettingsLabel =
    dictaphoneSettings != null ? cleanInputLabel(dictaphoneSettings.deviceLabel) : '';
  const deviceLabelMatchesDictaphone =
    audioInputDeviceLabel !== '' && cleanedInputLabel === cleanedSettingsLabel;
  const matchingDictaphoneProducts = useMemo(
    () => identifyDictaphoneProducts({ deviceLabel: audioInputDeviceLabel }),
    [audioInputDeviceLabel]
  );
  const isVirtualDictaphoneSelected =
    audioInputDeviceId === VIRTUAL_DICTAPHONE_AUDIO_INPUT_DEVICE_ID;
  const isKnownDictaphoneProduct =
    isVirtualDictaphoneSelected ||
    Array.from(matchingDictaphoneProducts).some(
      ({ id }) => id !== VIRTUAL_DICTAPHONE_AUDIO_INPUT_DEVICE_ID
    );
  const isDictaphone =
    deviceLabelMatchesDictaphone || isKnownDictaphoneProduct || userDescribedDictaphone;

  const setAudioInputDevice = useCallback(
    (deviceId: string, audioInputDeviceLabel: string) => {
      setMicrophoneState((v) => ({
        ...v,
        audioInputDeviceId: deviceId,
        audioInputDeviceLabel,
      }));
    },
    [setMicrophoneState]
  );

  const setUserDescribedDictaphone = useCallback(
    (userDescribedDictaphone: boolean) => {
      setMicrophoneState((v) => ({
        ...v,
        userDescribedDictaphone,
      }));
    },
    [setMicrophoneState]
  );

  // reset the device id back to the default value if the stored preference cannot be found
  useEffect(() => {
    if (loaded === false) return;

    const foundDevice = audioInputDevices.some(({ deviceId }) => deviceId === audioInputDeviceId);
    if (foundDevice !== true && audioInputDeviceId !== defaultValue.audioInputDeviceId) {
      setAudioInputDevice(defaultValue.audioInputDeviceId, defaultValue.audioInputDeviceLabel);
    }
  }, [loaded, audioInputDeviceId, audioInputDevices, setAudioInputDevice]);

  return useMemo(
    () => ({
      audioInputDevice,
      audioInputDeviceId,
      audioInputDeviceLabel,
      audioInputDevices,
      setAudioInputDevice,
      isKnownDictaphoneProduct,
      userDescribedDictaphone,
      setUserDescribedDictaphone,
      isDictaphone,
      isLoaded: loaded,
    }),
    [
      audioInputDevice,
      audioInputDeviceId,
      audioInputDeviceLabel,
      audioInputDevices,
      isKnownDictaphoneProduct,
      isDictaphone,
      userDescribedDictaphone,
      setUserDescribedDictaphone,
      setAudioInputDevice,
      loaded,
    ]
  );
}
